import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../config/api";
import "./Codings.css";
import { formatDate } from "../utils/dateUtils";
import {
  useCodingFilters,
  type Coding,
  type CodingFilters,
} from "../hooks/useCodingFilters";
import Select from "react-select";
import { toast } from "react-toastify";
import { errorMessage } from "../utils/errors";
import { syncSummary } from "../utils/syncSummary";
import { syncEntity } from "../utils/syncEntity";
import {
  formatCodingStatus,
  formatCodingType,
  formatTaskType,
} from "../utils/codingUtils";
import { EMPTY_VALUE } from "../utils/display";
import { darkSelectStyles } from "../utils/selectStyles";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/pagination";
import {
  assignmentFilterOptions,
  ASSIGNMENT_FILTER_PLACEHOLDER,
  LATE_ASSIGNMENT_LABEL,
  lateAssignmentFromParams,
} from "../utils/assignmentFilter";

/** What the API applies: the filters and the sort. */
type CodingQuery = Pick<
  CodingFilters,
  | "dateFrom"
  | "dateTo"
  | "coder"
  | "type"
  | "taskType"
  | "status"
  | "lateAssignment"
  | "sortBy"
  | "sortOrder"
>;

const Codings: React.FC = () => {
  const [codings, setCodings] = useState<Coding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Opened from the dashboard, the page carries its filter in the URL — the figure
  // clicked there and the rows shown here then describe the same set. Read once, as
  // the initial state: afterwards the dropdown owns the value.
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo(
    () => ({ lateAssignment: lateAssignmentFromParams(searchParams) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Use the filtering hook
  const {
    filters,
    filteredCodings,
    updateFilter,
    updateSort,
    resetFilters,
    hasActiveFilters,
  } = useCodingFilters(codings, initialFilters);

  // Dropdown choices come from the API so they cover every coding, not only the
  // ones on the current page.
  const [filterOptions, setFilterOptions] = useState<{
    coders: string[];
    types: string[];
    taskTypes: string[];
    statuses: string[];
  }>({ coders: [], types: [], taskTypes: [], statuses: [] });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await api.get("/api/codings/filter-options");
        setFilterOptions(response.data);
      } catch (error) {
        console.error("Error loading coding filter options:", error);
      }
    };
    loadFilterOptions();
  }, []);

  // Memoised so the effect below re-runs on a real change, not on every render.
  const activeQuery = useMemo<CodingQuery>(
    () => ({
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      coder: filters.coder,
      type: filters.type,
      taskType: filters.taskType,
      status: filters.status,
      lateAssignment: filters.lateAssignment,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    }),
    [
      filters.dateFrom,
      filters.dateTo,
      filters.coder,
      filters.type,
      filters.taskType,
      filters.status,
      filters.lateAssignment,
      filters.sortBy,
      filters.sortOrder,
    ],
  );

  // Only the very first load shows the page-level spinner; later fetches reload
  // the rows in place, since the spinner replaces the whole page.
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    fetchCodings(currentPage, pageSize, activeQuery, hasLoadedOnce.current);
    hasLoadedOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, activeQuery]);

  /**
   * `silent` reloads the rows in place: no page-level spinner, and a failure is
   * reported with a toast instead of replacing the page with the error view.
   * Used after a sync, where the page is already on screen.
   */
  const fetchCodings = async (
    page: number = 1,
    limit: number = DEFAULT_PAGE_SIZE,
    query: CodingQuery,
    silent = false,
  ) => {
    try {
      if (!silent) setLoading(true);
      // Empty filters are omitted so the API sees only the active ones.
      const activeFilters = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value),
      );
      const response = await api.get("/api/codings", {
        params: { page, limit, ...activeFilters },
      });
      setCodings(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.total);
      setCurrentPage(response.data.pagination.page);
    } catch (error) {
      if (silent) {
        toast.error("Erreur lors du rechargement des codages");
      } else {
        setError("Failed to load codings");
      }
      console.error("Error loading codings:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Any filter change re-queries the whole table, so the current page number is
  // meaningless against the new result set.
  const handleFilterChange = (key: keyof CodingFilters, value: string) => {
    updateFilter(key, value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    resetFilters();
    setCurrentPage(1);
  };

  // A new sort reorders the whole table, so the current page number no longer
  // points at the same rows.
  const handleSortChange = (column: CodingFilters["sortBy"]) => {
    updateSort(column);
    setCurrentPage(1);
  };

  const syncCodings = async () => {
    try {
      setSyncing(true);

      const data = await syncEntity("codings");

      if (data.success) {
        // Refresh the rows in place, keeping the active filters
        await fetchCodings(currentPage, pageSize, activeQuery, true);
        toast.success(syncSummary(data));
      } else {
        toast.error("Échec de la synchronisation : " + (data.message ?? ""));
      }
    } catch (error) {
      toast.error("Échec de la synchronisation : " + errorMessage(error));
      console.error("Sync error:", error);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Chargement des codages...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-error">
        <div className="alert alert-error">{error}</div>
        <button
          className="btn-primary"
          onClick={() => fetchCodings(currentPage, pageSize, activeQuery)}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="codings-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h1 className="page-title">Codages</h1>
            </div>
            <div className="header-actions">
              <button
                className={`sync-button ${syncing ? "syncing" : ""}`}
                onClick={syncCodings}
                disabled={syncing}
              >
                {syncing ? "Synchronisation..." : "Synchroniser Codages"}
              </button>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Date de début</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Date de fin</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Codeur</label>
              <Select
                value={
                  filters.coder
                    ? { value: filters.coder, label: filters.coder }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("coder", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Tous les codeurs" },
                  ...filterOptions.coders.map((coder) => ({
                    value: coder,
                    label: coder,
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Tous les codeurs"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Type</label>
              <Select
                value={
                  filters.type
                    ? {
                        value: filters.type,
                        label: formatCodingType(filters.type),
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("type", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Tous les types" },
                  ...filterOptions.types.map((type) => ({
                    value: type,
                    label: formatCodingType(type),
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Tous les types"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Tâche</label>
              <Select
                value={
                  filters.taskType
                    ? {
                        value: filters.taskType,
                        label: formatTaskType(filters.taskType),
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("taskType", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Toutes les tâches" },
                  ...filterOptions.taskTypes.map((taskType) => ({
                    value: taskType,
                    label: formatTaskType(taskType),
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Toutes les tâches"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Statut</label>
              <Select
                value={
                  filters.status
                    ? {
                        value: filters.status,
                        label: formatCodingStatus(filters.status),
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("status", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Tous les statuts" },
                  ...filterOptions.statuses.map((status) => ({
                    value: status,
                    label: formatCodingStatus(status),
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Tous les statuts"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Affectation</label>
              <Select
                value={
                  filters.lateAssignment
                    ? {
                        value: filters.lateAssignment,
                        label: LATE_ASSIGNMENT_LABEL,
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange(
                    "lateAssignment",
                    option ? option.value : "",
                  )
                }
                options={assignmentFilterOptions()}
                styles={darkSelectStyles}
                placeholder={ASSIGNMENT_FILTER_PLACEHOLDER}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <button
                onClick={handleResetFilters}
                className="btn-secondary reset-filters"
                disabled={!hasActiveFilters}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="codings-table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Match</th>
                  <th
                    className={`sortable ${
                      filters.sortBy === "type" ? filters.sortOrder : ""
                    }`}
                    onClick={() => handleSortChange("type")}
                  >
                    Type
                  </th>
                  <th
                    className={`sortable ${
                      filters.sortBy === "taskType" ? filters.sortOrder : ""
                    }`}
                    onClick={() => handleSortChange("taskType")}
                  >
                    Tâche
                  </th>
                  <th
                    className={`sortable ${
                      filters.sortBy === "date" ? filters.sortOrder : ""
                    }`}
                    onClick={() => handleSortChange("date")}
                  >
                    Date
                  </th>
                  <th
                    className={`sortable ${
                      filters.sortBy === "coder" ? filters.sortOrder : ""
                    }`}
                    onClick={() => handleSortChange("coder")}
                  >
                    Codeur
                  </th>
                  <th
                    className={`sortable ${
                      filters.sortBy === "status" ? filters.sortOrder : ""
                    }`}
                    onClick={() => handleSortChange("status")}
                  >
                    Statut
                  </th>
                  <th>Contrat</th>
                </tr>
              </thead>
              <tbody>
                {filteredCodings.map((coding) => (
                  <tr key={coding.id}>
                    <td>{coding.aia_codage_id}</td>
                    <td>{coding.aia_game_name}</td>
                    <td>{formatCodingType(coding.aia_codage_type)}</td>
                    <td>{formatTaskType(coding.aia_codage_task_type)}</td>
                    <td>{formatDate(coding.aia_codage_Date)}</td>
                    <td>
                      {coding.coder
                        ? `${coding.coder.first_name} ${coding.coder.last_name}`
                        : EMPTY_VALUE}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${(
                          coding.metadata?.external_status || "unknown"
                        )
                          .toLowerCase()
                          .replace(/\s+/g, "_")}`}
                      >
                        {formatCodingStatus(coding.metadata?.external_status)}
                      </span>
                    </td>
                    <td>
                      {coding.contract ? (
                        <Link
                          className="contract-link"
                          to={`/contracts?contractId=${coding.contract.id}`}
                          title={`Voir le contrat ${coding.contract.id}`}
                        >
                          {coding.contract.id}
                        </Link>
                      ) : (
                        EMPTY_VALUE
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Affichage de {(currentPage - 1) * pageSize + 1} à{" "}
                {Math.min(currentPage * pageSize, totalItems)} sur {totalItems}{" "}
                codages
              </span>
              <div className="page-size-selector">
                <label>Éléments par page</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                title="Première page"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                title="Page précédente"
              >
                ‹
              </button>
              <div className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination-btn ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                title="Page suivante"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                title="Dernière page"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Codings;
